import { InfoCircleOutlined } from '@ant-design/icons';
import { CrawlRun } from '@import-io/types';
import ProgressTracker from '@zeus/core/ProgressTracker';
import UserProfile from '@zeus/core/UserProfile';
import SnapshotViewLink from '_/components/Snapshots/SnapshotViewLink';
import { SNAPSHOT_STATUS_COLOR_MAPPING, SnapshotStatus } from '_/features/snapshots/common/snapshot.constants';
import { endpoints } from '_/lib/frontend-utils';
import { ISnapshot } from '_/types/snapshots/snapshot.types';
import { formatDateTime } from '_/utils/dateUtils';
import { formatNumber, basicSort, compareAssigneeSort, compareSort } from '_/utils/formatUtils';
import zeusSdk from '_/utils/SDKs';
import { snapshotNeedsAttentionOptions } from '_/utils/snapshots';
import { Checkbox, Tag, Tooltip, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import React from 'react';

import { isValidS3Url, parseS3Url } from './s3Utils';

export function getCrawlRunEstimation(crawlRun: CrawlRun, snapshot: ISnapshot): string | null {
  const totalUrlsInCrawl = crawlRun.totalUrlCount ?? 0;
  const totalUrlsFinished = (crawlRun.successUrlCount || 0) + (crawlRun.failedUrlCount || 0);

  if (totalUrlsFinished === 0) {
    return null;
  }

  // Calculate the rate at which things have previously been processed.
  const startDate = new Date(snapshot.startedAt);
  let totalTimeElapsed = Date.now() - startDate.getTime();
  totalTimeElapsed = totalTimeElapsed || 1;

  const previousCrawlRate = totalUrlsFinished / totalTimeElapsed;

  // Estimate time from crawl rate - allow 30 sec cushion bc of updates
  let estimatedTime = (totalUrlsInCrawl - totalUrlsFinished) / previousCrawlRate;
  estimatedTime = estimatedTime < 30000 && estimatedTime > 0 ? 30000 : estimatedTime;

  const days = Math.floor(estimatedTime / (1000 * 60 * 60 * 24));
  const dayStr = days ? days + ' day(s) ' : '';
  if (dayjs(estimatedTime).isValid() && estimatedTime !== 0) {
    return dayStr + dayjs.utc(estimatedTime).format('HH:mm:ss') + 's';
  } else {
    return null;
  }
}

export function determineCustomFiles(destinations, fileType) {
  return destinations.some(
    (destination) => destination.files[fileType] === true || destination.files?.archive?.[fileType] === true,
  );
}

export function determineIfOkayState(snapshotStatus: string) {
  const badStates = [
    SnapshotStatus.FAILED,
    SnapshotStatus.SUPERCEDED,
    SnapshotStatus.NO_LOGIN_AVAILABLE,
    SnapshotStatus.START_FAILED,
    SnapshotStatus.INTERNAL_FAILURE,
    SnapshotStatus.CANCEL_REQUESTED,
    SnapshotStatus.CANCEL_QUEUED,
    SnapshotStatus.CANCELLED,
  ];

  return !badStates.includes(snapshotStatus);
}

export function attachRollupStats(
  snapshots: ISnapshot[],
  snapshotsRollup: Record<string, { health?: Record<string, { sum: number; weightedAvg: number }> }> = {},
) {
  return snapshots.map((snapshot) => {
    const health = {};
    const rollupHealth = snapshotsRollup[snapshot.id]?.health || {};
    const sumMetrics = ['rows', 'pages', 'failedOptionalTests', 'failedRequiredTests'];

    Object.entries(rollupHealth).forEach(
      ([key, stats]) => (health[key] = sumMetrics.includes(key) ? formatNumber(stats.sum) : formatNumber(stats.weightedAvg)),
    );

    snapshot.health = health;
    return snapshot;
  });
}

export function formatHealth(snapshot: ISnapshot) {
  Object.entries(snapshot.health || {}).forEach(([key, stat]) => (snapshot.health[key] = formatNumber(stat)));
  return snapshot;
}

export function formatSnapshotHealth(snapshots: ISnapshot[]) {
  return snapshots?.map(formatHealth);
}

export function formatAssetUrl(url: string, assetType: string) {
  if (isValidS3Url(url)) {
    const parsed = parseS3Url(url);
    if (parsed?.Key) {
      return `https://crawl-assets.${endpoints.rootDomain}/${assetType}/${parsed.Key}`;
    }
  }

  return url;
}

export async function getRootSnapshots(
  id: string,
  level: string | number = 'deliveries',
  limit: number | boolean,
  currentCursor: string | null = null,
): Promise<{ items: ISnapshot[]; cursor: string }> {
  // TODO only including Collection and Project here for routing,
  // Can remove after ZEUS-619
  const options = {
    filter: 'parentId||null',
    queryParameters: {
      limit: limit || 100,
      sort: 'addedAt',
      sortDirection: 'DESC',
      cursor: currentCursor,
    },
    include: 'Source.Collection.Project,importStatus,editor,assignedUser,parent',
  };
  const { queryParameters, ...restOptions } = options;

  const api = zeusSdk[level];

  // Fetch 100 root snapshots at a time and get their rollup data
  const firstResults = await api.getChildren(id, 'snapshots', options);
  const firstRollup = await zeusSdk.snapshots.rollup(firstResults.items.map((snapshot) => snapshot.id));

  let allSnapshots = attachRollupStats(firstResults.items, firstRollup) || [];
  let cursor = firstResults.metadata.cursor;

  while (cursor) {
    if (limit && allSnapshots.length === limit) {
      break;
    }
    const results = await api.getChildren(id, 'snapshots', {
      ...restOptions,
      queryParameters: { ...queryParameters, cursor: cursor },
    });

    const snapshotsRollup = await zeusSdk.snapshots.rollup(results.items.map((snapshot) => snapshot.id));
    allSnapshots = [...allSnapshots, ...attachRollupStats(results.items, snapshotsRollup)];
    cursor = results.metadata.cursor;
  }

  return {
    items: allSnapshots,
    cursor: cursor,
  };
}

export async function getRecentSnapshots(
  statuses: string[] = [],
  currentCursor: string | null = null,
  snapshotId: string = '',
): Promise<{ items: ISnapshot[]; cursor: string }> {
  const options: Record<string, any> = {
    queryParameters: {
      limit: 50,
      sort: 'addedAt',
      sortDirection: 'DESC',
      cursor: currentCursor,
    },
    include: 'Source.Collection.Project,Source.Collection.Project.Org,importStatus,editor,assignedUser,parent',
  };

  const filters: string[] = [];
  if (statuses.length > 0) {
    filters.push(`status||in||${statuses.join(',')}`);
  }
  if (snapshotId) {
    filters.push(`id||eq||${snapshotId}`);
  }
  if (filters.length > 0) {
    options.filter = filters.join('||AND||');
  }

  const result = await zeusSdk.admin.listSnapshots(options);
  const items = result.items || [];
  const cursor = result.metadata?.cursor;

  return {
    items: formatSnapshotHealth(items),
    cursor: cursor,
  };
}

export const SNAPSHOT_ROWS_COLUMN: ColumnType<ISnapshot> = {
  title: 'Rows',
  dataIndex: ['health', 'rows'],
  align: 'left',
  render: (_, snapshot: ISnapshot) =>
    (snapshot.status === 'IN_PROGRESS' ? snapshot.importStatus?.crawlRun?.rowCount : snapshot.health?.rows) ?? '',
  sorter: basicSort(['health', 'rows']),
  sortDirections: ['ascend', 'descend'],
  width: 100,
};

export const DEFAULT_SNAPSHOT_TABLE_COLUMNS: ColumnType<ISnapshot>[] = [
  {
    title: 'Time in Status',
    dataIndex: 'statusUpdatedAt',
    align: 'left',
    sorter: compareSort('statusUpdatedAt'),
    sortDirections: ['ascend', 'descend'],
    width: 120,
    render: (_, snapshot: ISnapshot) => (snapshot.statusUpdatedAt ? dayjs(snapshot.statusUpdatedAt).fromNow(true) : null),
  },
  {
    title: 'Inputs',
    dataIndex: 'inputs',
    align: 'left',
    sorter: basicSort('inputs'),
    sortDirections: ['ascend', 'descend'],
    width: 100,
  },
  {
    title: 'Breached Threshold',
    dataIndex: 'inputsTrimmed',
    align: 'left',
    sorter: basicSort('inputsTrimmed'),
    sortDirections: ['ascend', 'descend'],
    width: 140,
    render: (_: any, { chunk, inputsTrimmed, parent }: ISnapshot) =>
      chunk == null ? inputsTrimmed : <Checkbox checked={!!(inputsTrimmed || parent?.inputsTrimmed)} />,
  },
  SNAPSHOT_ROWS_COLUMN,
  {
    title: '% 200',
    dataIndex: ['health', 'okPct'],
    align: 'left',
    sorter: basicSort(['health', 'okPct']),
    sortDirections: ['ascend', 'descend'],
    width: 140,
  },
  {
    title: '% 404/410',
    dataIndex: ['health', 'notFoundPct'],
    align: 'left',
    sorter: basicSort(['health', 'notFoundPct']),
    sortDirections: ['ascend', 'descend'],
    width: 140,
  },
  {
    title: '% Blocked',
    dataIndex: ['health', 'blockedPct'],
    align: 'left',
    sorter: basicSort(['health', 'blockedPct']),
    sortDirections: ['ascend', 'descend'],
    width: 140,
  },
  {
    title: '% Error',
    dataIndex: ['health', 'errorPct'],
    align: 'left',
    sorter: basicSort(['health', 'errorPct']),
    sortDirections: ['ascend', 'descend'],
    width: 140,
  },
  {
    title: '% HTML missing',
    dataIndex: ['health', 'noHtmlPct'],
    align: 'left',
    sorter: basicSort(['health', 'noHtmlPct']),
    sortDirections: ['ascend', 'descend'],
    width: 140,
  },
  {
    title: 'Failed Required Checks',
    dataIndex: ['health', 'failedRequiredTests'],
    align: 'left',
    sorter: basicSort(['health', 'failedRequiredTests']),
    sortDirections: ['ascend', 'descend'],
    width: 180,
  },
  {
    title: 'Failed Optional Checks',
    dataIndex: ['health', 'failedOptionalTests'],
    align: 'left',
    sorter: basicSort(['health', 'failedOptionalTests']),
    sortDirections: ['ascend', 'descend'],
    width: 180,
  },
  {
    title: 'Added',
    dataIndex: 'addedAt',
    align: 'left',
    sorter: compareSort('addedAt'),
    sortDirections: ['ascend', 'descend'],
    width: 140,
    render: (date: string) =>
      date ? (
        <div>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>
            {dayjs(date).utc().format('YYYY-MM-DD')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>{dayjs(date).utc().format('HH:mm:ss')}</Typography.Text>
        </div>
      ) : null,
  },
  {
    title: 'Updated',
    dataIndex: 'updatedAt',
    align: 'left',
    sorter: compareSort('updatedAt'),
    sortDirections: ['ascend', 'descend'],
    width: 140,
    render: (date: string) =>
      date ? (
        <div>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>
            {dayjs(date).utc().format('YYYY-MM-DD')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>{dayjs(date).utc().format('HH:mm:ss')}</Typography.Text>
        </div>
      ) : null,
  },
  {
    title: 'Started',
    dataIndex: 'startedAt',
    align: 'left',
    sorter: compareSort('startedAt'),
    sortDirections: ['ascend', 'descend'],
    width: 140,
    render: (date: string) =>
      date ? (
        <div>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>
            {dayjs(date).utc().format('YYYY-MM-DD')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>{dayjs(date).utc().format('HH:mm:ss')}</Typography.Text>
        </div>
      ) : null,
  },
  {
    title: 'Stopped At',
    dataIndex: 'stoppedAt',
    align: 'left',
    sorter: compareSort('stoppedAt'),
    sortDirections: ['ascend', 'descend'],
    width: 140,
    render: (date: string) =>
      date ? (
        <div>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>
            {dayjs(date).utc().format('YYYY-MM-DD')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>{dayjs(date).utc().format('HH:mm:ss')}</Typography.Text>
        </div>
      ) : null,
  },
  {
    title: 'Collect By',
    dataIndex: 'collectBy',
    align: 'left',
    sorter: compareSort('collectBy'),
    sortDirections: ['ascend', 'descend'],
    width: 140,
    render: (date: string) =>
      date ? (
        <div>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>
            {dayjs(date).utc().format('YYYY-MM-DD')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>{dayjs(date).utc().format('HH:mm:ss')}</Typography.Text>
        </div>
      ) : null,
  },
  {
    title: 'Deliver By',
    dataIndex: 'deliverBy',
    align: 'left',
    sorter: compareSort('deliverBy'),
    sortDirections: ['ascend', 'descend'],
    width: 140,
    render: (date: string) =>
      date ? (
        <div>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>
            {dayjs(date).utc().format('YYYY-MM-DD')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>{dayjs(date).utc().format('HH:mm:ss')}</Typography.Text>
        </div>
      ) : null,
  },
  {
    title: 'Must Finish By',
    dataIndex: 'mustFinishBy',
    align: 'left',
    sorter: compareSort('mustFinishBy'),
    sortDirections: ['ascend', 'descend'],
    width: 160,
    render: (date: string) =>
      date ? (
        <div>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>
            {dayjs(date).utc().format('YYYY-MM-DD')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '12px', display: 'block' }}>{dayjs(date).utc().format('HH:mm:ss')}</Typography.Text>
        </div>
      ) : null,
  },
];

export const SNAPSHOT_VIEW_COLUMN: ColumnType<ISnapshot> = {
  title: 'Link',
  dataIndex: 'id',
  align: 'left',
  width: 75,
  render: (_, snapshot) => <SnapshotViewLink snapshot={snapshot} />,
};

const setSnapshotStatusTag = (status) => {
  return <Tag color={SNAPSHOT_STATUS_COLOR_MAPPING[status]}>{status}</Tag>;
};

export const SNAPSHOT_STATUS_COLUMN: ColumnType<ISnapshot> = {
  title: 'Status',
  dataIndex: 'status',
  align: 'left',
  width: 180,
  sorter: compareSort('status'),
  sortDirections: ['ascend', 'descend'],
  render: (_, snapshot) =>
    snapshot.status === 'IN_PROGRESS' && snapshot.importStatus ? (
      <ProgressTracker
        size={'small'}
        completed={
          (snapshot.importStatus?.crawlRun?.failedUrlCount ?? 0) + (snapshot.importStatus?.crawlRun?.successUrlCount ?? 0)
        }
        total={snapshot.importStatus.crawlRun?.totalUrlCount || 0}
        showInfo={false}
        showTooltip
      />
    ) : (
      setSnapshotStatusTag(snapshot.status)
    ),
};

export const SNAPSHOT_ASSIGNEE_COLUMN: ColumnType<ISnapshot> = {
  title: 'Assignee',
  dataIndex: 'assigneeId',
  align: 'left',
  render: (_, record) => <UserProfile tableMode showDetails user={record.assignedUser} />,
  width: 220,
  sorter: compareAssigneeSort,
  sortDirections: ['ascend', 'descend'],
};

export const SNAPSHOT_LEGACY_COLUMN: ColumnType<ISnapshot> = {
  title: 'Legacy',
  dataIndex: 'isLegacyCrawlRun',
  align: 'left',
  render: (value: boolean) => (value ? <Checkbox checked={value} /> : null),
  sorter: basicSort('isLegacyCrawlRun'),
  sortDirections: ['ascend', 'descend'],
  width: 100,
};

const snapshotInfoIcon = (_, snapshot: ISnapshot) => {
  const status = snapshot.status;
  if (snapshot.needsAttention && status === SnapshotStatus.PENDING_QA) {
    const pendingQATime =
      snapshotNeedsAttentionOptions?.pendingQATime === 1
        ? `1 ${snapshotNeedsAttentionOptions?.time}`
        : `${snapshotNeedsAttentionOptions?.pendingQATime} ${snapshotNeedsAttentionOptions?.time}s`;

    return (
      <Tooltip title={`This snapshot has been in PENDING_QA for more than ${pendingQATime}.`}>
        <InfoCircleOutlined />
      </Tooltip>
    );
  } else if (snapshot.needsAttention && status === SnapshotStatus.QA) {
    const qaTime =
      snapshotNeedsAttentionOptions?.qaTime === 1
        ? `1 ${snapshotNeedsAttentionOptions?.time}`
        : `${snapshotNeedsAttentionOptions?.qaTime} ${snapshotNeedsAttentionOptions?.time}s`;

    return (
      <Tooltip title={`This snapshot has been in QA for more than ${qaTime}.`}>
        <InfoCircleOutlined />
      </Tooltip>
    );
  }

  return <></>;
};

export const SNAPSHOT_INFO_ICON: ColumnType<ISnapshot> = {
  // title: 'Icon',
  width: 50,
  render: (text, snapshot) => snapshotInfoIcon(text, snapshot),
};

const addElements = (field, value) => {
  const transformFields = ['_htmlExtraction', '_screenCapture'];

  if (field != '_screenCapture' && (/image/i.test(field) || /\.(jpg|gif|png)([?#]|$)/i.test(value))) {
    return (
      <a href={value} target="_blank">
        <img src={value} style={{ maxWidth: '10vw' }} />
      </a>
    );
  } else if (/^https?:/.test(value)) {
    if (transformFields.includes(field)) {
      const type = value.includes('html') ? 'html' : 'screenshot';
      return (
        <a href={formatAssetUrl(value, type)} target="_blank" style={{ textDecoration: 'underline' }}>
          Link
        </a>
      );
    } else {
      return (
        <a href={value} target="_blank" style={{ textDecoration: 'underline' }}>
          Link
        </a>
      );
    }
  } else if (/timestamp/i.test(field) || (value < 1896134400000 && value > 1517443200000)) {
    return formatDateTime(value);
  } else {
    return value;
  }
};

export const SNAPSHOT_CHECKS_COL_DEFS = [
  {
    title: 'Field',
    dataIndex: 'key',
  },
  {
    title: 'Value',
    dataIndex: 'value',
    render: (text, record) => {
      const field = record.key;
      if (Array.isArray(text)) {
        return (
          <ul style={{ marginBottom: 0 }}>
            {text?.map((val) => (
              <li>{addElements(field, val)}</li>
            ))}
          </ul>
        );
      } else {
        return addElements(field, text);
      }
    },
  },
];

export const SNAPSHOT_RETRY_OPTIONS = (
  snapshot,
  access,
  { disableReExtract, disableReImport, disableReRun, disableRerunTransforms, disableRegenerateAssets },
) => {
  const RE_EXTRACT = 'Re-extract Snapshot';
  const RE_IMPORT = 'Re-import Snapshot';
  const RE_RUN = 'Re-run Snapshot';
  const RE_RUN_TRANSFORMS = 'Re-run Transforms';
  const REGENERATE_ASSETS = 'Regenerate Custom Assets';
  const IMPORT = 'Import Data';

  return [
    {
      title: IMPORT,
      value: 'import',
      disabled: !access || snapshot.status !== SnapshotStatus.CANCELLED || !snapshot.crawlRunId,
    },
    {
      title: RE_EXTRACT,
      value: 'recalculateData',
      disabled: !access || disableReExtract,
    },
    {
      title: RE_IMPORT,
      value: 'reimport',
      disabled: !access || disableReImport,
    },
    {
      title: RE_RUN,
      value: 'rerunQueries',
      disabled: !access || disableReRun,
    },
    {
      title: RE_RUN_TRANSFORMS,
      value: 'rerunTransforms',
      disabled: !access || disableRerunTransforms,
    },
    {
      title: REGENERATE_ASSETS,
      value: 'regenerateAssets',
      disabled: !access || disableRegenerateAssets,
    },
  ];
};
